import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Input,
  Select,
  Tooltip,
  Typography,
  Upload,
  Avatar,
  Form,
  message,
} from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  ArrowLeftOutlined,
  CheckCircleTwoTone,
  DeleteOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWhatsappNumbers } from '../../../reduxToolkit/channelSlice';
import {
  daily_messaging_limit,
  quality_rating,
  categories,
} from '../../channels/constant/phoneNumbers';
import SignalStrength from '../../channels/page/whatsapp/whatsappBusinessPhoneNumbers/SignalStrength';
import { useFileUploader } from '../../../hooks/useFileUploader';
import {
  selectIsChatbotConfigured,
  fetchCrmTeamMembers,
  selectCrmTeamMembers,
  selectCrmTeamMembersLoading,
  selectAgentConfigSaving,
} from '../../../reduxToolkit/corpusSlice';
import { preSendUrl } from '../../../api/Api';

const { Title, Text } = Typography;
const { Dragger } = Upload;

const agentCategories = [
  { value: 'sales', label: 'Sales', disabled: true },
  { value: 'support', label: 'Customer support' },
];

const industryOptions = categories.map((cat) => ({
  value: cat.value,
  label: (
    <div className="flex items-center gap-2">
      {cat.icon && <cat.icon />}
      <span>{cat.name}</span>
    </div>
  ),
}));

const toneOptions = [
  { value: 'friendly', label: 'Friendly & warm' },
  { value: 'formal', label: 'Formal & concise' },
  // { value: 'playful', label: 'Playful & witty' },
  // { value: 'minimal', label: 'Minimal & straight to the point' },
];

const goalsByCategory = {
  sales: [{ value: 'sales', label: 'Sell our product to the customer' }],
  support: [
    { value: 'Resolve customer support queries', label: 'Resolve customer support queries' }
  ],
};

const responseStyleOptions = [
  { value: 'crisp', label: 'Crisp and concise' },
  { value: 'detailed', label: 'Detailed with examples' },
  { value: 'empathetic', label: 'Empathetic and reassuring' },
];

// const assigneeOptions = [
//   { value: 'assignee1', label: 'John (john@example.com)' },
//   { value: 'assignee2', label: 'Siva (siva@example.com)' },
//   { value: 'assignee3', label: 'Fullah (shaiFullah@example.com)' },
// ];

const HandoffHelpText = () => (
  <div className="max-w-xs">
    <p>
      Select an assignee who will be responsible for handling conversations when
      the agent needs human assistance.
    </p>
  </div>
);

const AgentConfigWizard = ({
  onComplete,
  onClose,
  showHeading = true,
  initialConfig = {},
  isFirstSetup = false,
}) => {
  const dispatch = useDispatch();
  const { currentBrand } = useSelector((state) => state.app);
  const isChatbotConfigured = useSelector(selectIsChatbotConfigured);
  const crmTeamMembers = useSelector(selectCrmTeamMembers);
  const crmTeamMembersLoading = useSelector(selectCrmTeamMembersLoading);
  const isSaving = useSelector(selectAgentConfigSaving);
  const { whatsappBusinessNumbersList } = useSelector((state) => state.Channel);
  const numbers = whatsappBusinessNumbersList?.wapp_business_num || [];
  const [docUploading, setDocUploading] = useState(false);
  const [uploadedDocUrl, setUploadedDocUrl] = useState('');
  const [brandingFileChanged, setBrandingFileChanged] = useState(false);

  const clearFiles = () => {
    setUploadedDocUrl('');
    setDocUploading(false);
  };

  const [formState, setFormState] = useState({
    phone: undefined,
    category: 'support',
    industry: industryOptions[0]?.value,
    goal: goalsByCategory['support']?.[0]?.value,
    tone: toneOptions[0]?.value,
    handoff: {},
    businessOverview: '',
    welcomeMessage: '',
    responseStyle: responseStyleOptions[0]?.value,
    clarifyingRules: '',
    disallowedTopics: '',
    sensitiveDataRules: '',
    brandingFile: '',
    businessName: '',
    address: '',
    website: '',
    about: '',
    description: '',
  });

  const [form] = Form.useForm();
  const [validationErrors, setValidationErrors] = useState({});

  const validateBrandFile = (file) => {
    // Check if file is empty
    if (file.size === 0) {
      message.error(`${file.name} is empty. Please select a valid file.`);
      return false;
    }

    // File type and size limits (in bytes) - Only PDF and TXT allowed, max 2MB
    const fileTypes = {
      '.pdf': 2 * 1024 * 1024, // PDF file (Maximum 2 MB)
      '.txt': 2 * 1024 * 1024, // Text file (Maximum 2 MB)
    };

    // Get file extension
    const fileExt = file.name
      .substring(file.name.lastIndexOf('.'))
      .toLowerCase();

    // Check if file type is supported
    if (!fileTypes[fileExt]) {
      message.error(
        `File type ${fileExt} is not supported. Only PDF and TXT files are allowed.`,
      );
      return false;
    }

    // Check file size limit
    const maxSize = fileTypes[fileExt];
    if (file.size > maxSize) {
      message.error(
        `File size exceeds the maximum limit of 2MB for ${fileExt} files.`,
      );
      return false;
    }

    // If all validations pass, return true to proceed with upload
    return true;
  };
  const navigate = useNavigate();

  useEffect(() => {
    if (initialConfig && Object.keys(initialConfig || {}).length) {
      setFormState((prev) => ({
        ...prev,
        ...initialConfig,
        phone: initialConfig.phone || prev.phone,
        category: initialConfig.category || prev.category,
        industry: initialConfig.industry || prev.industry,
        goal: initialConfig.goal || prev.goal,
        tone: initialConfig.tone || prev.tone,
        handoff: initialConfig.handoff || prev.handoff,
        responseStyle: initialConfig.responseStyle || prev.responseStyle,
        brandingFile: initialConfig.brandingFile || prev.brandingFile,
      }));
    }
  }, [initialConfig]);

  useEffect(() => {
    const validGoals =
      goalsByCategory[formState.category]?.map((g) => g.value) || [];
    if (!validGoals.includes(formState.goal)) {
      setFormState((prev) => ({ ...prev, goal: validGoals[0] }));
    }
  }, [formState.category, formState.goal]);

  useEffect(() => {
    if (!currentBrand?.brand_id) return;
    if (!numbers?.length) {
      dispatch(fetchWhatsappNumbers(currentBrand.brand_id));
    }
    dispatch(fetchCrmTeamMembers({ brandId: currentBrand.brand_id }));
  }, [currentBrand?.brand_id, numbers?.length, dispatch]);

  useEffect(() => {
    if (!formState.phone && numbers?.length) {
      const firstNumber =
        numbers.find(
          (num) =>
            num.uuid ||
            num.id ||
            num.send_from_phone_number_id ||
            num.send_from_phone_number,
        ) || numbers[0];
      const value =
        firstNumber?.uuid ||
        firstNumber?.id ||
        firstNumber?.send_from_phone_number_id ||
        firstNumber?.send_from_phone_number;
      setFormState((prev) => ({ ...prev, phone: value }));
    }
  }, [numbers, formState.phone]);

  const phoneOptions = useMemo(
    () =>
      numbers.map((num) => {
        const value =
          num?.uuid ||
          num?.id ||
          num?.send_from_phone_number_id ||
          num?.send_from_phone_number;
        const qualityLabel = quality_rating[num?.quality_rating] || 'Unknown';
        const dailyLimit = daily_messaging_limit[num?.daily_messaging_limit];
        return {
          value,
          raw: num,
          label: (
            <div className="flex flex-col py-1">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">
                  {num?.send_from_phone_name || 'Business number'}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
                <span className="font-semibold text-gray-800">
                  (+{num?.country_code}) {num?.send_from_phone_number}
                </span>
                <span className="flex items-center gap-1">
                  <SignalStrength level={qualityLabel?.toUpperCase()} />
                  {qualityLabel}
                </span>
                <span className="font-medium text-gray-700">
                  {dailyLimit ? `${dailyLimit} / day` : 'Limit unavailable'}
                </span>
              </div>
            </div>
          ),
        };
      }),
    [numbers],
  );

  const selectedProfile = useMemo(
    () =>
      numbers.find((item) => {
        const value =
          item?.uuid ||
          item?.id ||
          item?.send_from_phone_number_id ||
          item?.send_from_phone_number;
        return String(value) === String(formState.phone);
      }),
    [numbers, formState.phone],
  );

  const assigneeOptions = useMemo(() => {
    return crmTeamMembers.map((member) => ({
      value: member.id,
      key: member.id,
      label: (
        <div className="flex items-center gap-2">
          <Avatar
            src={member.profile_photo}
            size="small"
            style={{
              backgroundColor: !member.profile_photo ? '#f56a00' : '',
            }}
          >
            {member.first_name?.[0]?.toUpperCase()}
            {member.last_name?.[0]?.toUpperCase()}
          </Avatar>
          <span>
            {member.first_name} {member.last_name} ({member.email})
          </span>
        </div>
      ),
    }));
  }, [crmTeamMembers]);

  const validateForm = () => {
    const errors = {};
    const requiredFields = [
      { key: 'phone', label: 'WhatsApp business number' },
      { key: 'category', label: 'Agent type' },
      { key: 'industry', label: 'Industry' },
      { key: 'goal', label: 'Agent goal' },
      { key: 'tone', label: 'Brand voice / tone' },
      { key: 'handoff', label: 'Human handoff logic' },
      { key: 'businessOverview', label: 'Business overview' },
      { key: 'welcomeMessage', label: 'Welcome message' },
      { key: 'responseStyle', label: 'Response style' },
      { key: 'clarifyingRules', label: 'Clarifying questions' },
      { key: 'disallowedTopics', label: 'Disallowed topics' },
      { key: 'sensitiveDataRules', label: 'Sensitive data rules' },
      { key: 'brandingFile', label: 'Brand dictionary' },
      { key: 'businessName', label: 'Business name' },
      { key: 'address', label: 'Address' },
      { key: 'website', label: 'Website' },
      { key: 'about', label: 'About' },
      { key: 'description', label: 'Description' },
    ];

    requiredFields.forEach(({ key, label }) => {
      if (
        !formState[key] ||
        (typeof formState[key] === 'string' && formState[key].trim() === '')
      ) {
        errors[key] = `${label} is required`;
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isFormValid = useMemo(() => {
    const requiredFields = [
      'phone',
      'category',
      'industry',
      'goal',
      'tone',
      'handoff',
      'businessOverview',
      'welcomeMessage',
      'responseStyle',
      'clarifyingRules',
      'disallowedTopics',
      'sensitiveDataRules',
      'brandingFile',
      'businessName',
      'address',
      'website',
      'about',
      'description',
    ];
    return requiredFields.every((field) => {
      const value = formState[field];
      return value && (typeof value !== 'string' || value.trim() !== '');
    });
  }, [formState]);

  useEffect(() => {
    if (selectedProfile?.profile_info?.vertical) {
      setFormState((prev) => ({
        ...prev,
        industry: prev.industry || selectedProfile.profile_info.vertical,
      }));
    }
    if (selectedProfile) {
      setFormState((prev) => ({
        ...prev,
        businessName:
          prev.businessName || selectedProfile.send_from_phone_name || '',
        address: prev.address || selectedProfile.profile_info?.address || '',
        website:
          prev.website || selectedProfile.profile_info?.websites?.[0] || '',
        about: prev.about || selectedProfile.profile_info?.about || '',
        description:
          prev.description || selectedProfile.profile_info?.description || '',
        ...(selectedProfile.configuration || {}),
        goal: goalsByCategory['support']?.[0]?.label,
      }));
    }
  }, [selectedProfile]);

  //   }
  // }, [selectedProfile?.profile_info?.vertical, selectedProfile?.configuration]);

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }
    onComplete?.({
      ...formState,
      brandingFile: uploadedDocUrl || formState.brandingFile,
      brandingFileChanged,
    });
  };

  return (
    <div className="flex flex-col gap-4">
      {showHeading && (
        <div className="flex items-center justify-between gap-4">
          <div>
            <Title level={4} className="!mb-1 text-gray-900">
              Configure your WhatsApp agent
            </Title>
            <Text type="secondary">
              Fill in the identity, branding memory, behavior, and guardrails.
            </Text>
          </div>
          {/* {onClose && (
            <Button icon={<ArrowLeftOutlined />} onClick={onClose}>
              Close
            </Button>
          )} */}
        </div>
      )}
      <Card className="shadow-sm">
        <div className="space-y-8">
          <div className="space-y-3">
            <Title level={5} className="!mb-0 text-gray-900">
              Agent Identity
            </Title>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Text type="secondary">
                  Agent type <span className="text-red-500">*</span>
                </Text>
                <Select
                  className="mt-1 w-full"
                  value={formState.category}
                  options={agentCategories}
                  onChange={(value) => {
                    setFormState((prev) => ({ ...prev, category: value }));
                    if (validationErrors.category) {
                      setValidationErrors((prev) => ({
                        ...prev,
                        category: '',
                      }));
                    }
                  }}
                  status={validationErrors.category ? 'error' : ''}
                />
                {validationErrors.category && (
                  <Text type="danger" className="text-xs">
                    {validationErrors.category}
                  </Text>
                )}
              </div>
              <div>
                <Text type="secondary">
                  Industry <span className="text-red-500">*</span>
                </Text>
                <Select
                  className="mt-1 w-full"
                  placeholder="Select industry"
                  value={formState.industry}
                  options={industryOptions}
                  onChange={(value) => {
                    setFormState((prev) => ({ ...prev, industry: value }));
                    if (validationErrors.industry) {
                      setValidationErrors((prev) => ({
                        ...prev,
                        industry: '',
                      }));
                    }
                  }}
                  status={validationErrors.industry ? 'error' : ''}
                />
                {validationErrors.industry && (
                  <Text type="danger" className="text-xs">
                    {validationErrors.industry}
                  </Text>
                )}
              </div>
            </div>
            <div>
              <Text type="secondary">
                Agent goal <span className="text-red-500">*</span>
              </Text>
              <Select
                className="mt-1 w-full"
                value={formState.goal}
                options={goalsByCategory[formState.category] || []}
                onChange={(value) => {
                  console.log({value});
                  
                  setFormState((prev) => ({ ...prev, goal: value }));
                  if (validationErrors.goal) {
                    setValidationErrors((prev) => ({ ...prev, goal: '' }));
                  }
                }}
                status={validationErrors.goal ? 'error' : ''}
              />
              {validationErrors.goal && (
                <Text type="danger" className="text-xs">
                  {validationErrors.goal}
                </Text>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <Title level={5} className="!mb-0 text-gray-900">
              Branding Memory
            </Title>
            <div className="space-y-4">
              <div className="space-y-2">
                <Text type="secondary">
                  WhatsApp business number{' '}
                  <span className="text-red-500">*</span>
                </Text>
                <Select
                  className="w-full h-min"
                  value={formState.phone}
                  options={phoneOptions}
                  optionLabelProp="label"
                  onChange={(value) => {
                    setFormState((prev) => ({ ...prev, phone: value }));
                    if (validationErrors.phone) {
                      setValidationErrors((prev) => ({ ...prev, phone: '' }));
                    }
                  }}
                  placeholder="Select a WhatsApp business number"
                  disabled={!isFirstSetup}
                  status={validationErrors.phone ? 'error' : ''}
                />
                {validationErrors.phone && (
                  <Text type="danger" className="text-xs">
                    {validationErrors.phone}
                  </Text>
                )}
                <div className="rounded-xl border border-dashed- border-gray-200 p-4 space-y-3">
                  <Text className="block text-sm text-gray-500">
                    Your agent will reply from this number and inherit its
                    business profile.
                  </Text>
                  <Card
                    className="border-gray-200"
                    title={
                      <div className="flex items-center gap-2">
                        <CheckCircleTwoTone twoToneColor="#52c41a" />
                        <Text className="font-medium">Business profile</Text>
                      </div>
                    }
                    // extra={
                    //   <Button
                    //     type="link"
                    //     className="p-0"
                    //     onClick={() =>
                    //       navigate(
                    //         '/user/channels/whatsapp/business-phone-numbers',
                    //       )
                    //     }
                    //   >
                    //     Edit Details
                    //   </Button>
                    // }
                  >
                    {selectedProfile ? (
                      <div className="grid gap-3 md:grid-cols-2">
                        <div>
                          <Text type="secondary">
                            Business name{' '}
                            <span className="text-red-500">*</span>
                          </Text>
                          <Input
                            value={formState.businessName}
                            onChange={(e) => {
                              setFormState((prev) => ({
                                ...prev,
                                businessName: e.target.value,
                              }));
                              if (validationErrors.businessName) {
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  businessName: '',
                                }));
                              }
                            }}
                            placeholder="Business Name"
                            status={
                              validationErrors.businessName ? 'error' : ''
                            }
                          />
                          {validationErrors.businessName && (
                            <Text type="danger" className="text-xs">
                              {validationErrors.businessName}
                            </Text>
                          )}
                        </div>
                        <div>
                          <Text type="secondary">
                            Address <span className="text-red-500">*</span>
                          </Text>
                          <Input
                            value={formState.address}
                            onChange={(e) => {
                              setFormState((prev) => ({
                                ...prev,
                                address: e.target.value,
                              }));
                              if (validationErrors.address) {
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  address: '',
                                }));
                              }
                            }}
                            placeholder="Address"
                            status={validationErrors.address ? 'error' : ''}
                          />
                          {validationErrors.address && (
                            <Text type="danger" className="text-xs">
                              {validationErrors.address}
                            </Text>
                          )}
                        </div>
                        <div>
                          <Text type="secondary">
                            Website <span className="text-red-500">*</span>
                          </Text>
                          <Input
                            value={formState.website}
                            onChange={(e) => {
                              setFormState((prev) => ({
                                ...prev,
                                website: e.target.value,
                              }));
                              if (validationErrors.website) {
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  website: '',
                                }));
                              }
                            }}
                            placeholder="Website"
                            status={validationErrors.website ? 'error' : ''}
                          />
                          {validationErrors.website && (
                            <Text type="danger" className="text-xs">
                              {validationErrors.website}
                            </Text>
                          )}
                        </div>
                        <div className="w-full">
                          <Text type="secondary">
                            About <span className="text-red-500">*</span>
                          </Text>
                          <Input.TextArea
                            rows={2}
                            value={formState.about}
                            onChange={(e) => {
                              setFormState((prev) => ({
                                ...prev,
                                about: e.target.value,
                              }));
                              if (validationErrors.about) {
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  about: '',
                                }));
                              }
                            }}
                            placeholder="About"
                            status={validationErrors.about ? 'error' : ''}
                          />
                          {validationErrors.about && (
                            <Text type="danger" className="text-xs">
                              {validationErrors.about}
                            </Text>
                          )}
                        </div>
                        <div className="w-full">
                          <Text type="secondary">
                            Description <span className="text-red-500">*</span>
                          </Text>
                          <Input.TextArea
                            rows={2}
                            value={formState.description}
                            onChange={(e) => {
                              setFormState((prev) => ({
                                ...prev,
                                description: e.target.value,
                              }));
                              if (validationErrors.description) {
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  description: '',
                                }));
                              }
                            }}
                            placeholder="Description"
                            status={validationErrors.description ? 'error' : ''}
                          />
                          {validationErrors.description && (
                            <Text type="danger" className="text-xs">
                              {validationErrors.description}
                            </Text>
                          )}
                        </div>
                      </div>
                    ) : (
                      <Text type="secondary">
                        Select a number to view details.
                      </Text>
                    )}
                  </Card>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Text type="secondary">
                    Business overview <span className="text-red-500">*</span>
                  </Text>
                  <Input.TextArea
                    rows={6}
                    value={formState.businessOverview}
                    onChange={(e) => {
                      setFormState((prev) => ({
                        ...prev,
                        businessOverview: e.target.value,
                      }));
                      if (validationErrors.businessOverview) {
                        setValidationErrors((prev) => ({
                          ...prev,
                          businessOverview: '',
                        }));
                      }
                    }}
                    placeholder="What should the agent know about the business?"
                    status={validationErrors.businessOverview ? 'error' : ''}
                  />
                  {validationErrors.businessOverview && (
                    <Text type="danger" className="text-xs">
                      {validationErrors.businessOverview}
                    </Text>
                  )}
                </div>
                <div className="flex gap-2 flex-col">
                  <div>
                    <Text type="secondary">
                      Brand voice / tone <span className="text-red-500">*</span>
                    </Text>
                    <Select
                      className="mt-1 w-full"
                      value={formState.tone}
                      options={toneOptions}
                      onChange={(value) => {
                        setFormState((prev) => ({ ...prev, tone: value }));
                        if (validationErrors.tone) {
                          setValidationErrors((prev) => ({
                            ...prev,
                            tone: '',
                          }));
                        }
                      }}
                      status={validationErrors.tone ? 'error' : ''}
                    />
                    {validationErrors.tone && (
                      <Text type="danger" className="text-xs">
                        {validationErrors.tone}
                      </Text>
                    )}
                  </div>
                  <div>
                    <Text type="secondary">
                      Welcome message <span className="text-red-500">*</span>
                    </Text>
                    <Input.TextArea
                      rows={3}
                      value={formState.welcomeMessage}
                      onChange={(e) => {
                        setFormState((prev) => ({
                          ...prev,
                          welcomeMessage: e.target.value,
                        }));
                        if (validationErrors.welcomeMessage) {
                          setValidationErrors((prev) => ({
                            ...prev,
                            welcomeMessage: '',
                          }));
                        }
                      }}
                      placeholder="First message the agent sends to greet users."
                      status={validationErrors.welcomeMessage ? 'error' : ''}
                    />
                    {validationErrors.welcomeMessage && (
                      <Text type="danger" className="text-xs">
                        {validationErrors.welcomeMessage}
                      </Text>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2">
                  <Text type="secondary">
                    Brand dictionary <span className="text-red-500">*</span>
                  </Text>
                  <Tooltip title="A file describing your business, tone, products, and FAQs">
                    <QuestionCircleOutlined className="text-gray-400" />
                  </Tooltip>
                </div>

                {formState.brandingFile ? (
                  <div className="flex items-center justify-between gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <CheckCircleTwoTone twoToneColor="#52c41a" />
                      <Text className="text-sm text-green-700 truncate">
                        {formState.brandingFile.split('/').pop() ||
                          formState.brandingFile}
                      </Text>
                    </div>
                    <Button
                      size="small"
                      danger
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        clearFiles();
                        setBrandingFileChanged(true);
                        setFormState((prev) => ({ ...prev, brandingFile: '' }));
                        if (validationErrors.brandingFile) {
                          setValidationErrors((prev) => ({
                            ...prev,
                            brandingFile: '',
                          }));
                        }
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <Dragger
                    name="file"
                    multiple={false}
                    showUploadList={false}
                    accept=".pdf,.txt"
                    beforeUpload={(file) => {
                      const isValid = validateBrandFile(file);
                      return isValid || Upload.LIST_IGNORE;
                    }}
                    customRequest={async ({ file, onSuccess, onError }) => {
                      try {
                        if (!file || file.size === 0) {
                          message.error('Selected file is empty.');
                          onError?.(new Error('Empty file'));
                          return;
                        }

                        setDocUploading(true);

                        const ext = file.name.split('.').pop()?.toLowerCase();
                        const fileName = file.name;

                        const presign = await preSendUrl(ext, {
                          brand_id: currentBrand?.brand_id,
                          path: 'data-sources',
                          file_name: fileName,
                        });

                        if (
                          !(presign?.status === 200 && presign?.data?.response)
                        ) {
                          throw new Error('Failed to prepare upload.');
                        }

                        const fileTypeMap = {
                          pdf: 'application/pdf',
                          txt: 'text/plain',
                          doc: 'application/msword',
                          docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        };

                        const contentType =
                          fileTypeMap[ext] ||
                          file.type ||
                          'application/octet-stream';

                        const putResp = await fetch(presign.data.response, {
                          method: 'PUT',
                          headers: { 'Content-Type': contentType },
                          body: file,
                        });

                        if (!putResp.ok) throw new Error('Upload failed');

                        const uploadedUrl = putResp.url?.split('?').shift();
                        if (!uploadedUrl)
                          throw new Error('Could not resolve uploaded URL');

                        setUploadedDocUrl(uploadedUrl);
                        setFormState((prev) => ({
                          ...prev,
                          brandingFile: uploadedUrl,
                        }));

                        if (validationErrors.brandingFile) {
                          setValidationErrors((prev) => ({
                            ...prev,
                            brandingFile: '',
                          }));
                        }

                        message.success(`${file.name} uploaded successfully`);
                        onSuccess?.({}, file);
                      } catch (err) {
                        console.error('Upload error:', err);
                        message.error(
                          `Upload failed: ${err.response?.data?.message || err.message || 'Please try again.'}`,
                        );
                        onError?.(err);
                      } finally {
                        setDocUploading(false);
                      }
                    }}
                    className="!p-6"
                    disabled={docUploading}
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">
                      {docUploading
                        ? 'Uploading...'
                        : 'Drop your PDF/TXT file (max 2MB) or click to browse'}
                    </p>
                    <p className="ant-upload-hint text-gray-500">
                      Only PDF and TXT files are supported (maximum 2MB).
                    </p>
                  </Dragger>
                )}

                {validationErrors.brandingFile && (
                  <Text type="danger" className="text-xs">
                    {validationErrors.brandingFile}
                  </Text>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Title level={5} className="!mb-0 text-gray-900">
              Behavior Settings
            </Title>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Text type="secondary">
                  Response style <span className="text-red-500">*</span>
                </Text>
                <Select
                  className="mt-1 w-full"
                  value={formState.responseStyle}
                  options={responseStyleOptions}
                  onChange={(value) => {
                    setFormState((prev) => ({ ...prev, responseStyle: value }));
                    if (validationErrors.responseStyle) {
                      setValidationErrors((prev) => ({
                        ...prev,
                        responseStyle: '',
                      }));
                    }
                  }}
                  status={validationErrors.responseStyle ? 'error' : ''}
                />
                {validationErrors.responseStyle && (
                  <Text type="danger" className="text-xs">
                    {validationErrors.responseStyle}
                  </Text>
                )}
              </div>
              <div>
                <div className="flex items-center gap-1">
                  <Text type="secondary">
                    Human handoff logic <span className="text-red-500">*</span>
                  </Text>
                  <Tooltip title={<HandoffHelpText />}>
                    <QuestionCircleOutlined className="text-gray-400" />
                  </Tooltip>
                </div>
                <Select
                  className="mt-1 w-full"
                  value={formState.handoff?.id || undefined}
                  options={assigneeOptions}
                  loading={crmTeamMembersLoading}
                  onChange={(value) => {
                    const selectedUser =
                      crmTeamMembers.find((user) => user.id === value) || {};
                    setFormState((prev) => ({
                      ...prev,
                      handoff: {
                        id: selectedUser.id,
                        brand_user_id: selectedUser.brand_user_id,
                        user_name: selectedUser?.user_name || 'user-fallback',
                        email: selectedUser.email,
                      },
                    }));
                    if (validationErrors.handoff) {
                      setValidationErrors((prev) => ({ ...prev, handoff: '' }));
                    }
                  }}
                  placeholder="Pick an assignee"
                  status={validationErrors.handoff ? 'error' : ''}
                  optionLabelProp="label"
                />
                {validationErrors.handoff && (
                  <Text type="danger" className="text-xs">
                    {validationErrors.handoff}
                  </Text>
                )}
              </div>
            </div>
            <div>
              <Text type="secondary">
                Clarifying questions <span className="text-red-500">*</span>
              </Text>
              <Input.TextArea
                rows={3}
                value={formState.clarifyingRules}
                onChange={(e) => {
                  setFormState((prev) => ({
                    ...prev,
                    clarifyingRules: e.target.value,
                  }));
                  if (validationErrors.clarifyingRules) {
                    setValidationErrors((prev) => ({
                      ...prev,
                      clarifyingRules: '',
                    }));
                  }
                }}
                placeholder="clarifying questions"
                status={validationErrors.clarifyingRules ? 'error' : ''}
              />
              {validationErrors.clarifyingRules && (
                <Text type="danger" className="text-xs">
                  {validationErrors.clarifyingRules}
                </Text>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <Title level={5} className="!mb-0 text-gray-900">
              Compliance Guardrails
            </Title>
            <div>
              <Text type="secondary">
                Disallowed topics <span className="text-red-500">*</span>
              </Text>
              <Input.TextArea
                rows={3}
                value={formState.disallowedTopics}
                onChange={(e) => {
                  setFormState((prev) => ({
                    ...prev,
                    disallowedTopics: e.target.value,
                  }));
                  if (validationErrors.disallowedTopics) {
                    setValidationErrors((prev) => ({
                      ...prev,
                      disallowedTopics: '',
                    }));
                  }
                }}
                placeholder="Topics the agent must avoid (e.g., pricing negotiations, legal advice)"
                status={validationErrors.disallowedTopics ? 'error' : ''}
              />
              {validationErrors.disallowedTopics && (
                <Text type="danger" className="text-xs">
                  {validationErrors.disallowedTopics}
                </Text>
              )}
            </div>
            <div>
              <Text type="secondary">
                Sensitive data rules <span className="text-red-500">*</span>
              </Text>
              <Input.TextArea
                rows={3}
                value={formState.sensitiveDataRules}
                onChange={(e) => {
                  setFormState((prev) => ({
                    ...prev,
                    sensitiveDataRules: e.target.value,
                  }));
                  if (validationErrors.sensitiveDataRules) {
                    setValidationErrors((prev) => ({
                      ...prev,
                      sensitiveDataRules: '',
                    }));
                  }
                }}
                placeholder="Instructions for handling sensitive data (PII, payment info, health data)"
                status={validationErrors.sensitiveDataRules ? 'error' : ''}
              />
              {validationErrors.sensitiveDataRules && (
                <Text type="danger" className="text-xs">
                  {validationErrors.sensitiveDataRules}
                </Text>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3">
            {/* <Button onClick={() => onClose?.()}>Cancel</Button> */}
            <Tooltip
              title={
                !isFormValid
                  ? 'Please fill in all required fields marked with * to save.'
                  : ''
              }
            >
              <Button
                type="primary"
                onClick={handleSave}
                disabled={!isFormValid || isSaving}
                loading={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save configuration'}
              </Button>
            </Tooltip>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AgentConfigWizard;
